import BigNumber from 'bignumber.js'
import { DISPLAY_AMOUNT } from 'config/constants'
import { RouteConfig } from 'config/constants/route'
import { GameType } from 'config/types/game'
import { InhouseGameFakeBetDetails } from 'config/types/profile'
import Column from 'layout/Components/Column'
import { RowBetween, RowCenter, RowMiddle } from 'layout/Components/Row'
import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import InhouseGameService from 'services/InhouseGameService/InhouseGameService'
import { useProviderInfo } from 'state/app/hooks'
import { useUserSettings } from 'state/profile/hooks'
import { Icons } from 'svgs'
import theme from 'theme'
import Box from 'UIKit/Box/Box'
import Image from 'UIKit/Image'
import Link from 'UIKit/Link'
import { ModalProps } from 'UIKit/Modal'
import Text from 'UIKit/Text'
import { getFullDisplayBalance } from 'utils/formatBalance'
import { getGameTypeDisplay } from 'utils/games'
import { formatRoute } from 'utils/stringHelper'
import { getTokenName } from 'utils/token'
import TokenAmountFiatDisplay from 'views/Fiat/TokenAmountFiatDisplay'
import { InhouseGameProbilityFairBetInfo, LimboProbilityFairBetInfo } from 'views/InhouseGame/config/types'
import {
  DisplayMultiplier,
  FieldTitle,
  SectionTransactionAmount,
  TransactionWrapperModal,
  WrapperContainerTransactionDetail,
} from '../styled'
import ProbilityFairSession from './ProbilityFairSession'

const InhouseGameBetDetailsModal: React.FC<
  ModalProps<{
    betTxn: InhouseGameFakeBetDetails
  }>
> = ({ data, onDismiss }) => {
  const { betTxn } = data
  const { t } = useTranslation()
  const {
    userSettings: { isDisplayInFiat },
  } = useUserSettings()

  const [bet, setBet] = useState<InhouseGameProbilityFairBetInfo>(null)
  const provider = useProviderInfo(betTxn?.game?.providerCode)
  const formatMultiplier = new BigNumber(betTxn?.multiplier)
  const displayNumber = Number.isNaN(Number(betTxn.bet.amount))
    ? '0'
    : getFullDisplayBalance(new BigNumber(betTxn.bet.amount), 0, DISPLAY_AMOUNT)

  useEffect(() => {
    const fetch = async () => {
      const betDetails = await InhouseGameService.getBetInfo(betTxn?.providerBetIdRef)
      if (betDetails) {
        setBet(betDetails as LimboProbilityFairBetInfo)
      }
    }

    fetch()
  }, [betTxn?.providerBetIdRef])

  return (
    betTxn &&
    bet && (
      <TransactionWrapperModal title={t('Bet Details')} onDismiss={onDismiss} overflow="auto !important">
        <SectionTransactionAmount
          title={t('Payout')}
          amount={Number.isNaN(Number(bet.betAmount.amount.toString(10))) ? '0' : bet.payout.toString(10)}
          token={bet.betAmount.token}
          hideTokenName={isDisplayInFiat}
          className="section-with-background"
        >
          <RowBetween alignItems="flex-start !important">
            <FieldTitle>{t('Bet Amount')}</FieldTitle>
            <Text color="textTertiary" fontSize="16px" fontWeight={600}>
              {isDisplayInFiat ? (
                <TokenAmountFiatDisplay
                  tokenAmount={{
                    token: bet.betAmount.token,
                    amount: BigNumber(displayNumber),
                  }}
                />
              ) : (
                displayNumber
              )}
              {!isDisplayInFiat && (
                <Text
                  fontSize="1em"
                  ml="4px"
                  as="span"
                  fontWeight={600}
                  color="textAlt1"
                  style={{
                    wordBreak: 'keep-all',
                  }}
                >
                  {getTokenName(bet.betAmount.token)}
                </Text>
              )}
            </Text>
          </RowBetween>

          <RowBetween mt="12px">
            <FieldTitle>{t('Multiplier')}</FieldTitle>
            <DisplayMultiplier
              amount={bet.payout.gt(0) ? bet.payout.div(bet.betAmount.amount) : betTxn.multiplier}
              fontSize="16px"
              color={formatMultiplier.gt(0) ? 'success' : 'textAlt1'}
              bold
            />
          </RowBetween>
        </SectionTransactionAmount>
        <WrapperContainerTransactionDetail mt="12px" className="section-with-background">
          <RowBetween>
            <RowMiddle mr="16px">
              <Box borderRadius={theme.radii.tiny} overflow="hidden">
                <Image src={betTxn.game.thumbnail} width={48} height={48} />
              </Box>
              <Column ml="8px">
                <Text small color="textTertiary" fontWeight={500} lineHeight={1.3} ellipsis={1}>
                  {betTxn.game.name}
                </Text>
                <Text fontSize="12px" lineHeight={1.3} ellipsis={1} color="textAlt1">
                  {getGameTypeDisplay(betTxn.game.type) ? `${getGameTypeDisplay(betTxn.game.type)} | ` : ''}
                  {provider.name}
                </Text>
              </Column>
            </RowMiddle>

            <RowMiddle minWidth="70px">
              <Text
                as={Link}
                href={formatRoute(RouteConfig.GameDetail, { slug: betTxn.game.slug })}
                disabledDefaultStyle
                color="primary"
                fontSize="12px"
                mr="4px"
                onClick={() => {
                  onDismiss()
                }}
              >
                {t('Play Now')}
              </Text>
              <Icons.ArrowRight
                fill={theme.colors.primary}
                style={{
                  transform: 'scale(1.1)',
                }}
              />
            </RowMiddle>
          </RowBetween>
        </WrapperContainerTransactionDetail>
        {betTxn.game?.type === GameType.InHouse && (
          <WrapperContainerTransactionDetail mt="12px" className="section-with-background">
            <ProbilityFairSession
              inhouseGameType={betTxn.game.additionalData.inhouseGameType}
              inhouseBetId={betTxn.providerBetIdRef}
              betDetails={bet}
            />
          </WrapperContainerTransactionDetail>
        )}

        <RowCenter mt="12px">
          <Image src="/logo-text.png" width={120} height={32} alt="logo" />
        </RowCenter>
      </TransactionWrapperModal>
    )
  )
}

export default InhouseGameBetDetailsModal
